import { Request, Response, RequestHandler } from 'express';

const SERVER_PORT = process.env.SERVER_PORT || '3000';

/**
 * API 路由信息
 */
interface ApiRouteInfo {
  id: string;
  path: string;
  method: string;
  module: string;
}

/**
 * API 路由分组
 */
interface ApiRouteGroup {
  id: string;
  name: string;
  apis: ApiRouteInfo[];
}

/**
 * 后端 debug 接口返回的路由信息
 */
interface DebugRouteInfo {
  order: string;
  path: string;
  method: string;
  regexp?: string;
}

/**
 * 后端 debug 接口返回的完整响应
 */
interface DebugResponse {
  '路由配置': {
    total: number;
    routes: DebugRouteInfo[];
  };
  '进程存活时间（秒）': number;
  '进程启动时间（秒）': string;
  '运行时版本': Record<string, string>;
}

/**
 * 从路径中提取模块名
 */
function extractModuleFromPath(path: string): string {
  const segments = path.split('/').filter(Boolean);

  // 跳过 'api' 前缀
  let startIndex = 0;
  if (segments[0] === 'api') {
    startIndex = 1;
  }

  // 跳过版本号 (v1, v2, etc.)
  if (segments[startIndex]?.match(/^v\d+$/)) {
    startIndex++;
  }

  const moduleName = segments[startIndex];
  if (!moduleName || moduleName.startsWith(':')) {
    return 'default';
  }

  return moduleName;
}

/**
 * 生成路由唯一标识
 */
function generateRouteId(method: string, path: string): string {
  const cleanPath = path.replace(/[/:]/g, '_').replace(/^_+|_+$/g, '');
  return `${method.toLowerCase()}_${cleanPath}`;
}

/**
 * 首字母大写
 */
function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * 按模块分组路由
 */
function groupRoutesByModule(routes: ApiRouteInfo[]): ApiRouteGroup[] {
  const groupMap = new Map<string, ApiRouteInfo[]>();

  routes.forEach((route) => {
    const existing = groupMap.get(route.module) || [];
    existing.push(route);
    groupMap.set(route.module, existing);
  });

  const groups: ApiRouteGroup[] = [];

  groupMap.forEach((apis, moduleName) => {
    groups.push({
      id: moduleName,
      name: capitalize(moduleName),
      apis: apis.sort((a, b) => a.path.localeCompare(b.path)),
    });
  });

  return groups.sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * 从后端 debug 接口获取路由列表
 */
async function fetchRoutesFromBackend(basePath: string): Promise<ApiRouteInfo[]> {
  // basePath 例如 /af/p/app_xxx，需要拼接完整路径
  const normalizedBasePath = basePath.replace(/\/+$/, '');
  const url = `http://localhost:${SERVER_PORT}${normalizedBasePath}/api/__framework__/debug`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Accept': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch routes: ${response.status}`);
  }

  const data = (await response.json()) as DebugResponse;

  // 从 debug 接口响应中提取路由信息
  const routeConfig = data['路由配置'];
  if (!routeConfig || !routeConfig.routes || !Array.isArray(routeConfig.routes)) {
    console.warn('[api-list] Invalid routes data:', routeConfig);
    return [];
  }

  const debugRoutes = routeConfig.routes;

  return debugRoutes
    .filter((route: DebugRouteInfo) => {
      // 只保留包含 /api/ 的路由，排除内部路由和 capability 路由
      return route.path.includes('/api/') &&
        !route.path.includes('__framework__') &&
        !route.path.includes('__innerapi__') &&
        !route.path.includes('__platform__') &&
        !route.path.includes('/api/capability') &&
        !route.path.includes('/api/authnpaas') &&
        !route.path.includes('/api/sdk_innerapi');
    })
    .map((route: DebugRouteInfo) => {
      // 从完整路径中提取 /api/... 部分
      const apiPathMatch = route.path.match(/\/api\/.*/);
      const apiPath = apiPathMatch ? apiPathMatch[0] : route.path;

      const method = route.method || 'ALL';

      return {
        id: generateRouteId(method, apiPath),
        path: apiPath,
        method,
        module: extractModuleFromPath(apiPath),
      };
    });
}

/**
 * Create handler for /api-list route
 */
export function createApiListHandler(): RequestHandler {
  return async (_req: Request, res: Response) => {
    try {
      // 从环境变量或请求中获取 basePath
      // req.baseUrl 是当前 router 的挂载路径，如 /af/p/app_xxx/dev/logs
      // 我们需要提取 /af/p/app_xxx 部分
      const basePath = process.env.CLIENT_BASE_PATH || '';
      const routes = await fetchRoutesFromBackend(basePath);
      const groups = groupRoutesByModule(routes);

      res.json({
        groups,
        total: routes.length,
      });
    } catch (error) {
      console.error('[api-list] Failed to fetch routes:', error);
      res.status(500).json({
        error: 'Failed to fetch routes',
        message: error instanceof Error ? error.message : 'Unknown error',
      });
    }
  };
}
